import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import {
  getLocalWallet,
  fetchWallet,
  fetchPublicRewards,
  createWalletWithPhone,
  clearLocalWallet,
} from '../utils/wallet';

// ---------- Onboarding modal (phone required) ----------
const OnboardModal = ({ open, onCreated, recovery, initialPhone }) => {
  const [phone, setPhone] = useState(initialPhone || '');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const clean = phone.trim();
    if (clean.length < 6) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setBusy(true);
    try {
      const { wallet, existed } = await createWalletWithPhone(clean, name);
      toast.success(existed ? `Welcome back, ${wallet.name || wallet.short_code}` : 'Wallet ready · start earning points');
      onCreated(wallet);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not create wallet');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-amber-500/80 via-orange-500/80 to-rose-500/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl p-6 max-h-[95vh] overflow-y-auto shadow-2xl" data-testid="wallet-onboard-modal">
        <div className="w-12 h-1 bg-slate-200 rounded-full mx-auto mb-4 sm:hidden"></div>
        <div className="text-center mb-5">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center text-3xl mx-auto shadow-lg">🎁</div>
          <h2 className="text-2xl font-black mt-3">
            {recovery ? 'Restore your wallet' : 'Join A-Shop Rewards'}
          </h2>
          <p className="text-sm text-slate-500 mt-1 px-2">
            {recovery
              ? "Type your phone number — we'll load your points."
              : 'Earn points on every purchase. Just your phone — no password, no signup form.'}
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Phone number *</label>
            <input
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0612345678"
              className="w-full px-4 py-3.5 border-2 border-slate-200 rounded-xl focus:border-amber-500 focus:outline-none text-base"
              required
              autoFocus
              data-testid="onboard-phone-input"
            />
          </div>
          {!recovery && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">First name <span className="text-slate-400 normal-case">(optional)</span></label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sarah"
                className="w-full px-4 py-3.5 border-2 border-slate-200 rounded-xl focus:border-amber-500 focus:outline-none text-base"
                data-testid="onboard-name-input"
              />
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-black rounded-xl text-base disabled:opacity-60 shadow-lg"
            data-testid="onboard-submit-btn"
          >
            {busy
              ? <i className="fas fa-spinner fa-spin"></i>
              : (recovery ? 'Restore my wallet' : 'Create my wallet')}
          </button>
          <p className="text-[11px] text-center text-slate-400 pt-1 px-2">
            {recovery
              ? 'Same number as before? Your balance & history return instantly.'
              : "We only use it so the counter knows it's really you. No spam."}
          </p>
        </form>
      </div>
    </div>
  );
};

// ---------- Main wallet page ----------
const Wallet = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboard, setShowOnboard] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Always load the public menu — even before wallet exists.
      const menu = await fetchPublicRewards();
      setRewards(menu);

      const local = getLocalWallet();
      if (!local?.id) {
        setShowOnboard(true);
        setRecoveryMode(false);
        setLoading(false);
        return;
      }
      try {
        const full = await fetchWallet(local.id);
        setWallet(full);
        setShowOnboard(false);
      } catch (err) {
        if (err.notFound) {
          // Wallet was wiped server-side (rare). Clear and re-onboard.
          clearLocalWallet();
          setWallet(null);
          setRecoveryMode(true);
          setShowOnboard(true);
        } else {
          toast.error('Could not reach the server');
        }
      }
    } catch {
      toast.error('Could not load the rewards menu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // live-refresh points
    return () => clearInterval(t);
  }, []);

  const onCreated = async (w) => {
    setShowOnboard(false);
    setRecoveryMode(false);
    // Fetch full wallet (includes history)
    try {
      const full = await fetchWallet(w.id);
      setWallet(full);
    } catch {
      setWallet(w);
    }
  };

  const nextReward = rewards.find((r) => r.points_cost > (wallet?.points || 0));
  const progress = nextReward
    ? Math.min(100, Math.round(((wallet?.points || 0) / nextReward.points_cost) * 100))
    : 100;

  if (loading && !wallet && !showOnboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center">
        <i className="fas fa-spinner fa-spin text-2xl text-amber-500"></i>
      </div>
    );
  }

  // If onboarding is required, show it as the entire screen.
  if (showOnboard && !wallet) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <OnboardModal open={true} onCreated={onCreated} recovery={recoveryMode} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 text-slate-900" data-testid="wallet-page">
      {/* Top bar */}
      <div className="bg-white/70 backdrop-blur border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
            data-testid="wallet-back-btn"
            aria-label="Back"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-600">A-Shop</p>
            <h1 className="text-lg font-black">Rewards Wallet</h1>
          </div>
          <button
            onClick={load}
            className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
            aria-label="Refresh"
            data-testid="wallet-refresh-btn"
          >
            <i className="fas fa-arrows-rotate"></i>
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Balance + QR */}
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 text-white shadow-2xl p-6">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/10 rounded-full blur-3xl"></div>
          <div className="relative">
            <div className="flex items-start justify-between mb-6">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/70">Balance</p>
                <p className="text-6xl font-black leading-none mt-1" data-testid="wallet-balance">{wallet?.points || 0}</p>
                <p className="text-sm font-medium text-white/80 mt-1">
                  {wallet?.points === 1 ? 'point' : 'points'} · earned {wallet?.lifetime_earned || 0} total
                </p>
                {(wallet?.name || wallet?.phone) && (
                  <p className="text-xs text-white/80 mt-2 truncate">
                    <i className="fas fa-user-circle mr-1"></i>
                    {wallet?.name ? `${wallet.name} · ` : ''}{wallet?.phone}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">Code</p>
                <p className="text-2xl font-black tracking-wider mt-1" data-testid="wallet-code">{wallet?.short_code}</p>
              </div>
            </div>

            {/* QR */}
            <div className="bg-white rounded-2xl p-4 flex flex-col items-center shadow-inner">
              <QRCodeCanvas
                value={wallet?.id || ''}
                size={200}
                level="H"
                includeMargin={false}
                fgColor="#0f172a"
                bgColor="#ffffff"
                data-testid="wallet-qr"
              />
              <p className="text-xs text-slate-500 mt-3 text-center font-medium">
                Show this at the counter to earn or redeem
              </p>
            </div>

            {/* Progress */}
            {nextReward && (
              <div className="mt-5" data-testid="wallet-next-reward">
                <div className="flex items-center justify-between text-xs font-bold text-white/90 mb-2">
                  <span className="truncate mr-2">Next: {nextReward.emoji || '🎁'} {nextReward.title}</span>
                  <span className="shrink-0">{wallet?.points || 0}/{nextReward.points_cost}</span>
                </div>
                <div className="h-2 bg-white/25 rounded-full overflow-hidden">
                  <div className="h-full bg-white transition-all duration-500 rounded-full" style={{ width: `${progress}%` }}></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Rewards menu */}
        <div>
          <div className="flex items-center gap-2 mb-3 px-1">
            <i className="fas fa-gift text-amber-500"></i>
            <h2 className="text-lg font-black">Redeem your points</h2>
          </div>
          {rewards.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center text-slate-400 border border-slate-200">
              <i className="fas fa-hourglass-half text-2xl mb-2 opacity-60"></i>
              <p className="text-sm">Rewards coming soon — start earning points now!</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="wallet-rewards-list">
              {rewards.map((r) => {
                const canAfford = (wallet?.points || 0) >= r.points_cost;
                return (
                  <li
                    key={r.id}
                    className={`bg-white rounded-2xl border p-4 flex items-center gap-3 transition ${canAfford ? 'border-emerald-200 shadow-sm' : 'border-slate-200 opacity-70'}`}
                    data-testid={`wallet-reward-${r.id}`}
                  >
                    <div className="text-3xl">{r.emoji || '🎁'}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm">{r.title}</p>
                      {r.description && <p className="text-xs text-slate-500 mt-0.5">{r.description}</p>}
                    </div>
                    <div className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-black ${canAfford ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                      {r.points_cost} pts
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-xs text-slate-500 text-center mt-3 px-4">
            To redeem, show your QR at the counter and tell staff which reward you want.
          </p>
        </div>

        {/* Activity */}
        {wallet?.history && wallet.history.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3 px-1">
              <i className="fas fa-clock-rotate-left text-slate-500"></i>
              <h2 className="text-lg font-black">Recent activity</h2>
            </div>
            <ul className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="wallet-history">
              {wallet.history.slice(0, 10).map((h) => (
                <li key={h.id} className="px-4 py-3 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${h.type === 'earn' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                    <i className={`fas ${h.type === 'earn' ? 'fa-plus' : 'fa-gift'} text-xs`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{h.note || (h.type === 'earn' ? 'Points added' : 'Reward redeemed')}</p>
                    <p className="text-xs text-slate-400">{new Date(h.ts).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <span className={`font-black text-sm ${h.type === 'earn' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {h.type === 'earn' ? '+' : ''}{h.amount}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 pt-4 pb-8">
          <p className="font-bold">A-Shop Rewards · Since 2016</p>
          <p className="mt-1">Recovery code: <span className="font-mono font-black">{wallet?.short_code}</span> · Phone linked ✓</p>
          <button
            onClick={() => {
              if (!window.confirm('Sign out of this wallet on this device? Your points stay safe — restore anytime with your phone.')) return;
              clearLocalWallet();
              setWallet(null);
              setRecoveryMode(true);
              setShowOnboard(true);
            }}
            className="mt-3 text-xs text-slate-400 hover:text-slate-600 underline"
            data-testid="wallet-signout-btn"
          >
            Not you? Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Wallet;
